import React from "react";
import { useTranslation } from "react-i18next";
import { useSignalR } from "../../../context/SignalRContext";
import { formatRemaining, useCountdown } from "../../lib/dealClock";
import { useDealStatus } from "../../lib/dealStatus";

// How long the two connected parties have left before the deal expires for inactivity, shown in
// the deal-flow header. The same countdown already existed on the overview, so the parties could
// see it everywhere EXCEPT the pages they actually work on; this closes that gap.
// The status behind it is read-only (see lib/dealClock), so displaying the clock never extends it.
const URGENT_SECONDS = 5 * 60;

export default function DealExpiryCountdown() {
  const { t } = useTranslation();
  const { dealId } = useSignalR();
  const status = useDealStatus(dealId);
  const remaining = useCountdown(status?.dealRemainingSeconds, status?.readAt);

  // Only while a deal is actually running: a completed, cancelled or expired deal has no clock.
  if (!dealId || status?.dealState !== "in_progress" || remaining === null) return null;

  const urgent = remaining <= URGENT_SECONDS;
  const label = t("chooseAction.active.timeRemaining");

  return (
    <div
      className="inline-flex h-9 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium"
      style={{
        borderColor: urgent ? "rgba(255,183,153,.55)" : "rgba(255,255,255,.22)",
        color: urgent ? "#ffc3ad" : "inherit",
      }}
      data-testid="deal-expiry-countdown"
    >
      <span className="hidden opacity-70 sm:inline">{label}</span>
      <span role="timer" aria-label={label} style={{ fontVariantNumeric: "tabular-nums" }}>
        {formatRemaining(remaining)}
      </span>
    </div>
  );
}
